import { useState } from "react";

import { Loader2, Truck } from "lucide-react";

import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Label } from "~/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { useToast } from "~/hooks/use-toast";
import { useTransportistas } from "~/hooks/useTransportistas";
import { assignTransportista } from "~/services/transportistaService";

interface BulkTransportistaAssignmentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedOrderIds: string[];
  onSuccess: () => void;
}

export function BulkTransportistaAssignmentDialog({
  open,
  onOpenChange,
  selectedOrderIds,
  onSuccess,
}: BulkTransportistaAssignmentDialogProps) {
  const [selectedTransportistaId, setSelectedTransportistaId] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const { toast } = useToast();
  const { transportistas, isLoading } = useTransportistas();

  const handleConfirm = async () => {
    if (!selectedTransportistaId) {
      toast({
        title: "Error",
        description: "Debe seleccionar un transportista",
        variant: "destructive",
      });
      return;
    }

    setIsProcessing(true);
    let successCount = 0;
    let errorCount = 0;

    for (const orderId of selectedOrderIds) {
      try {
        await assignTransportista(orderId, selectedTransportistaId);
        successCount++;
      } catch (error) {
        console.error(`Error assigning transportista to order ${orderId}:`, error);
        errorCount++;
      }
    }

    setIsProcessing(false);

    if (successCount > 0 && errorCount === 0) {
      toast({
        title: "Transportista asignado",
        description: `Se asignó el transportista a ${successCount} pedido${successCount > 1 ? "s" : ""} exitosamente.`,
      });
      onSuccess();
      handleClose();
    } else if (successCount > 0) {
      toast({
        title: "Asignación parcial",
        description: `Se asignó el transportista a ${successCount} pedido${successCount > 1 ? "s" : ""}. ${errorCount} fallaron.`,
        variant: "destructive",
      });
      onSuccess();
      handleClose();
    } else {
      toast({
        title: "Error",
        description: "No se pudo asignar el transportista a ningún pedido.",
        variant: "destructive",
      });
    }
  };

  const handleClose = () => {
    setSelectedTransportistaId("");
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Truck className="h-5 w-5" />
            Asignar Transportista
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-lg bg-orange-50 p-4">
            <p className="text-sm text-orange-800">
              Se asignará el transportista seleccionado a{" "}
              <strong>
                {selectedOrderIds.length} pedido
                {selectedOrderIds.length > 1 ? "s" : ""}
              </strong>{" "}
              en tránsito.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="transportista-select">Seleccionar Transportista</Label>
            {isLoading ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                <span className="text-sm text-muted-foreground">
                  Cargando transportistas...
                </span>
              </div>
            ) : (
              <Select
                value={selectedTransportistaId}
                onValueChange={setSelectedTransportistaId}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccione un transportista" />
                </SelectTrigger>
                <SelectContent>
                  {transportistas.map((transportista) => (
                    <SelectItem key={transportista.id} value={transportista.id}>
                      {transportista.name} {transportista.lastname}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isProcessing}>
            Cancelar
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!selectedTransportistaId || isLoading || isProcessing}
            className="bg-orange-500 hover:bg-orange-600"
          >
            {isProcessing ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Asignando...
              </>
            ) : (
              <>
                <Truck className="mr-2 h-4 w-4" />
                Asignar
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
